/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import UserTable from "components/tables/UserTable";
import useHttp from "hooks/http.hook";
import { FC, useEffect, useState, useMemo } from "react";
import { QueryResultInterface, UserInterface } from "@malmur-technologies/medicalsystem-shared";
import { useNavigate } from "react-router-dom";
import { CoreAPI_URLs } from "constants/http.const";
import Box from "components/generals/Box";
import Button from "components/generals/Button";
import { useTranslation } from "react-i18next";
import { RoutesConstants } from "@malmur-technologies/medicalsystem-shared";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";
import TextField from "components/generals/TextField";
import useSort from "hooks/useSort.hook";
import { Autocomplete } from "@mui/material";
import { UserRolesEnum } from "@malmur-technologies/medicalsystem-shared";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { selectUserRoles } from "store/slices/auth.slice";
import { getCreatableRoles } from "utils/rolePermissions.utils";
/**
 * Content component for displaying and managing system users.
 * Provides a table view of all users with search, filtering, pagination,
 * and navigation to create new users.
 * @returns {React.ReactElement} The user data content component
 */
const UserDataContent: FC = () => {
  const [isLoading, _error, sendRequest] = useHttp();

  const [userList, setUserList] = useState<UserInterface[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowsTotal, setRowsTotal] = useState<number>(0);
  const [filterModel, setFilterModel] = useState<string>("");
  const { sortBy, sortOrder, handleSortChange, buildSortParam } = useSort();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userType, setUserType] = useState<UserRolesEnum>();
  function handleNewUser() {
    navigate(RoutesConstants.NEW_USER);
  }
  useEffect(() => {
    const nameParam = filterModel ? `username=${filterModel}&` : "";
    const roleParam = userType ? `roles=${userType}&` : "";
    const url = `${CoreAPI_URLs.USERS_GET_ALL_PERSONS}?${nameParam}${roleParam}${buildSortParam()}page=${page}&pageSize=${pageSize}`;

    sendRequest(url, onGetUserHandler, "GET");
  }, [page, pageSize, filterModel, buildSortParam, userType]);

  const onGetUserHandler = (userResponse: QueryResultInterface<UserInterface>) => {
    setUserList(userResponse.results);
    setRowsTotal(userResponse.total);
    setPageTotal(Math.ceil(userResponse.total / pageSize));
  };
    const currentUserRoles = useSelector((state: RootState) => selectUserRoles(state));
    const creatableRolesOptions = useMemo(() => {
      const roles = getCreatableRoles(currentUserRoles);
      return roles.map((role) => ({
        label: t(`userRoles.${role}`),
        value: role,
      }));
    }, [currentUserRoles, t]);
  
  return (
    <Box>
      <Box mt={2} mb={2} width="100%" display="flex" justifyContent="space-between">
              <Autocomplete
          disablePortal
          id="user-type-select"
          options={creatableRolesOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          sx={{ width: 300 }}
          onChange={(_, newValue) => {
            setUserType(newValue?.value);
          }}
          renderInput={(params) => <TextField {...params} label={t("userRoles.userType")} />}
        />
        <PermissionWrapper requiredPermissions={[PermissionsUser[EntityPermissions.USERS].CREATE]}>
          <Button id="cy-new-user-button" type="button" variant="contained" color="info" onClick={handleNewUser}>
            {t("userDataContent.newUser")}
          </Button>
        </PermissionWrapper>
      </Box>
      <UserTable
        modelList={userList}
        setModelList={setUserList}
        canSearchPlaceholder={t("userDataContent.searchUser")}
        entriesPerPage={{ defaultValue: pageSize }}
        setPageState={setPage}
        setPageSizeState={setPageSize}
        apiPagination={true}
        apiPageTotal={pageTotal}
        apiRowsTotal={rowsTotal}
        apiPageCurrentIndex={page}
        setModelFilter={setFilterModel}
        isLoading={isLoading}
        onSortChange={handleSortChange}
        currentSortBy={sortBy}
        currentSortOrder={sortOrder}
      />
    </Box>
  );
};

export default UserDataContent;
